import { ActivityTableRow } from '../types';
import { AUTHENTICATION_LOG_REASONS } from '@silverfort-nodelibs/common/utils/consts';
import { DENY_REASONS } from '@silverfort-nodelibs/common/utils/consts';
import { INVESTIGATION_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { INVESTIGATION_DRAWER_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { DIRECTORY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { RISK_LEVELS } from '@silverfort-nodelibs/common/utils/consts';
import { PRIVILEGED_ACCOUNTS_STATUS } from '@silverfort-nodelibs/common/utils/consts';
import { USER_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { RELYING_PARTY_SERVER_NAME } from '@silverfort-nodelibs/common/utils/consts';
import { isEmpty } from 'lodash';
import {
    formatDestination,
    formatRuleAction,
    formatUserResponse,
    getEnumLabelByValue,
    getExtraRiskIndicatorDescription,
    getLocationText,
    getRuleIdWithPrefix,
    isFeatureActive,
    riskLevelToText
} from '@silverfort-nodelibs/common/utils/common';
import { getRiskIndicatorByRiskRule } from '@silverfort-nodelibs/common/utils/insight';
import { AUTH_MODE_FILTERS_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { ACTIVITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { RISK_CHANGE_TYPE } from '@silverfort-nodelibs/common/utils/consts';
import { ReactNode } from 'react';
import { POLICY_ACTION_TYPES } from '@silverfort-nodelibs/common/utils/consts';

const getActivityTypeText = (data) => {
    if (data.activityType === ACTIVITY_TYPES.JIT_ACTION) {
        return 'JIT action';
    } else if (data.activityType === ACTIVITY_TYPES.AUTH_LOG) {
        return 'Authentication';
    } else if (data.activityType === ACTIVITY_TYPES.RISK_CHANGE) {
        return 'Risk change';
    } else if (data.riskChangeType === RISK_CHANGE_TYPE.LEVEL_CHANGE) {
        return 'Risk level change';
    } else if (data.riskChangeType === RISK_CHANGE_TYPE.INDICATOR_CHANGE) {
        return 'Risk indicator change';
    }
};

const getRelyingPartyName = (serverGuid, server, service, relyingParties) => {
    let relyingPartyName = server;
    if (relyingParties && relyingParties[serverGuid]) {
        relyingPartyName = relyingParties[serverGuid].name;
    } else if (service) {
        relyingPartyName = service;
    }
    return relyingPartyName;
};

const generateHeadline = ({ data, entityType, onEntityClick, defaultDestinations, relyingParties, entityData }) => {
    let isHost = false;
    let isDevice = false;
    let usernameSection: string | ReactNode = '';
    let hostNameSection: string | ReactNode = '';
    let serverSection: string | ReactNode = '';

    if (data.riskChangeType === RISK_CHANGE_TYPE.INDICATOR_CHANGE) {
        const riskIndicator = getRiskIndicatorByRiskRule(data.risk_indicator_type);
        const riskDescription = (
            riskIndicator?.entityTitle ||
            data.description ||
            data.risk_indicator_type
        ).toUpperCase();
        return {
            headline: `${riskDescription} - Risk indicator was ${data.is_deleted ? 'eliminated' : 'detected'}`,
            metadata: {
                type: 'indicator',
                risk_indicator_type: data.risk_indicator_type,
                is_deleted: data.is_deleted
            }
        };
    }

    if (data.riskChangeType === RISK_CHANGE_TYPE.LEVEL_CHANGE) {
        const riskLevel = Object.values(RISK_LEVELS)
            .find((r) => r.value === data.risk_level)
            ?.label.toUpperCase();
        const oldRiskLevel = Object.values(RISK_LEVELS)
            .find((r) => r.value === data.old_risk_level)
            ?.label.toUpperCase();
        return {
            headline: `Entity risk level was changed from ${oldRiskLevel} to ${riskLevel}`,
            metadata: { type: 'level_change', risk_level: data.risk_level, old_risk_level: data.old_risk_level }
        };
    }

    if (data.activityType === ACTIVITY_TYPES.JIT_ACTION) {
        const reason = data.reason || '';
        const itsmData = data.itsmData || {};
        return {
            headline: `${data.action === PRIVILEGED_ACCOUNTS_STATUS.Enabled ? 'Enabled' : 'Disabled'}${
                reason ? ` with reason ${reason}` : ''
            }${
                itsmData?.ticketID
                    ? ` after validating ticket ${itsmData.ticketID} in ${itsmData.itsmType}` +
                      (itsmData.approver && itsmData.approvalDate
                          ? ` (approved by ${itsmData.approver} at ${itsmData.approvalDate})`
                          : '')
                    : ''
            } (JIT policy)`,
            metadata: { type: 'jit_action', action: data.action, reason: reason }
        };
    }

    if (data.host_name && data.host_name.toLowerCase() !== 'n/a') {
        isHost = data.user_type !== USER_TYPES.MACHINE_ACCOUNT;
        if (isHost) {
            hostNameSection = data.host_guid ? (
                <span
                    className="hover:text-sf-primary-300 text-sf-primary-500 !cursor-pointer"
                    onClick={() =>
                        onEntityClick(data.host_guid, data.host_name, INVESTIGATION_DRAWER_ENTITY_TYPES.DEVICE.adType)
                    }>
                    {data.host_name}
                </span>
            ) : (
                data.host_name
            );
        }

        if (data.host_name.toLowerCase() === entityData.id) {
            isDevice = true;
        }
    }

    if (data.user_guid || data.user_identifier) {
        const userName = data.upn || data.user_identifier_display_name || data.user_identifier || data.user_guid;
        if (userName.toLowerCase() !== 'n/a') {
            const isMachineAccount = data.user_type === USER_TYPES.MACHINE_ACCOUNT;
            usernameSection = isMachineAccount ? (
                userName
            ) : (
                <span
                    className="hover:text-sf-primary-300 text-sf-primary-500 !cursor-pointer"
                    onClick={() =>
                        onEntityClick(
                            data.user_guid,
                            data.user_identifier,
                            INVESTIGATION_DRAWER_ENTITY_TYPES.USER.adType
                        )
                    }>
                    {userName}
                </span>
            );
        }
    }

    const [logDestination, isDefaultDestination] = formatDestination(
        data.server,
        data.server_ip,
        data.protocol_type,
        data.directory_type,
        defaultDestinations
    );

    if (logDestination) {
        if (data.server.toLowerCase() === RELYING_PARTY_SERVER_NAME) {
            serverSection = getRelyingPartyName(data.server_guid, data.server, data.service, relyingParties);
        } else if (isDefaultDestination || (!data.server_guid && !data.server)) {
            serverSection = logDestination;
        } else {
            serverSection = (
                <span
                    className="hover:text-sf-primary-300 text-sf-primary-500 !cursor-pointer"
                    onClick={() =>
                        onEntityClick(
                            data.server_guid || data.server,
                            data.server,
                            INVESTIGATION_DRAWER_ENTITY_TYPES.RESOURCE.adType
                        )
                    }>
                    {logDestination}
                </span>
            );
        }
    }

    const baseContext = {
        username: usernameSection,
        server: serverSection,
        host: hostNameSection,
        isHost,
        isDevice
    };

    let headlineText: string | ReactNode = 'Unknown policy type';
    const action = data.action;
    const response = data.user_response;
    const ruleId = data.effective_rule_id;
    const reason = data.reason;
    const errorCode = data.error_code;
    const policyType = data.policyType;

    switch (policyType) {
        case POLICY_ACTION_TYPES.ALLOW.value:
            if (entityType === INVESTIGATION_ENTITY_TYPES.RESOURCE.id) {
                if (isDevice) {
                    headlineText = (
                        <span>
                            Used {usernameSection ? 'by ' : ''}
                            {usernameSection ? usernameSection : ''} to access {serverSection}
                        </span>
                    );
                } else {
                    headlineText = (
                        <span>
                            Accessed {usernameSection ? 'by ' : ''}
                            {usernameSection ? usernameSection : ''} {isHost && 'from'} {hostNameSection}
                        </span>
                    );
                }
            } else {
                if (ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE) {
                    headlineText = (
                        <span>
                            Accessed {serverSection} {isHost && <>from {hostNameSection} </>}
                            Allow, as user is unpaired (per unpaired user policy).
                        </span>
                    );
                } else if (!ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE) {
                    headlineText = (
                        <span>
                            Accessed {serverSection} {isHost && <>from {hostNameSection} </>}
                            Allow, as user is not in an enrolled group.
                        </span>
                    );
                } else if (!ruleId) {
                    headlineText = (
                        <span>
                            Accessed {serverSection} {isHost && <>from {hostNameSection} </>}
                            Allow (no policy).
                        </span>
                    );
                } else {
                    headlineText = (
                        <span>
                            Accessed {serverSection} {isHost && <>from {hostNameSection} </>}
                            Allow, according to policy for user.
                        </span>
                    );
                }
            }
            break;

        case POLICY_ACTION_TYPES.DENY.value:
            let denyReason = '';
            if (entityType === INVESTIGATION_ENTITY_TYPES.RESOURCE.id) {
                const errorCodeText = data.error_code ? ` (${data.error_code})` : '';
                const reasonText = (
                    <span>
                        {data.reason && DENY_REASONS[data.reason]
                            ? ` due to ${DENY_REASONS[data.reason]}${errorCodeText}`
                            : ''}
                    </span>
                );
                if (isDevice) {
                    headlineText = (
                        <span>
                            Access attempt {usernameSection ? 'by ' : ''}
                            {usernameSection ? usernameSection : ''} to {serverSection} was Denied {reasonText}
                        </span>
                    );
                } else {
                    headlineText = (
                        <span>
                            Access attempt {usernameSection ? 'by ' : ''}
                            {usernameSection ? usernameSection : ''} {isHost && 'from'} {hostNameSection} was Denied{' '}
                            {reasonText}
                        </span>
                    );
                }
            } else {
                if (ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE) {
                    denyReason = 'as user is unpaired (per unpaired user policy).';
                } else if (!ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE) {
                    denyReason = 'as user is not in an enrolled group.';
                } else if (reason && DENY_REASONS[reason]) {
                    denyReason = `due to ${DENY_REASONS[reason]}` + (errorCode ? ` (${errorCode})` : '');
                } else {
                    denyReason = 'according to policy for user.';
                }
                headlineText = (
                    <span>
                        Attempted to access {serverSection} {isHost && <>from {hostNameSection} </>}
                        Blocked, {denyReason}
                    </span>
                );
            }
            break;

        case POLICY_ACTION_TYPES.MFA.value:
            const [userResponse] = formatUserResponse(response, data.effective_rule_action);
            const responseNote = userResponse !== 'n/a' ? ` MFA Response: ${userResponse}` : '';

            if (entityType === INVESTIGATION_ENTITY_TYPES.RESOURCE.id) {
                if (isDevice) {
                    if (data.action) {
                        headlineText = (
                            <span>
                                Used {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} to access {serverSection} and validated by{' '}
                                <span>
                                    Multi-Factor authentication
                                    {data.user_response === AUTHENTICATION_LOG_REASONS.SAME_GROUP.USER_RESPONSE
                                        ? ' based on Policy Group.'
                                        : '.'}
                                </span>
                                {responseNote}
                            </span>
                        );
                    } else {
                        headlineText = (
                            <span>
                                Access attempt {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} to {serverSection} was Denied - Multi-Factor
                                authentication
                                <span>{' validation failed.'}</span>
                                {responseNote}
                            </span>
                        );
                    }
                } else {
                    if (data.action) {
                        headlineText = (
                            <span>
                                Accessed {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''} {isHost && 'from'} {hostNameSection} and
                                validated by{' '}
                                <span>
                                    Multi-Factor authentication
                                    {data.user_response === AUTHENTICATION_LOG_REASONS.SAME_GROUP.USER_RESPONSE
                                        ? ' based on Policy Group.'
                                        : '.'}
                                    {responseNote}
                                </span>
                            </span>
                        );
                    } else {
                        headlineText = (
                            <span>
                                Access attempt {usernameSection ? 'by ' : ''}
                                {usernameSection ? usernameSection : ''}
                                {`was Denied - Multi-Factor authentication validation failed.`} {responseNote}
                            </span>
                        );
                    }
                }
            } else {
                if (action) {
                    headlineText = (
                        <span>
                            Accessed {serverSection} {isHost ? 'from ' : ''} {hostNameSection} and validated by MFA.{' '}
                            {responseNote}
                        </span>
                    );
                } else if (response === AUTHENTICATION_LOG_REASONS.TIMEOUT) {
                    headlineText = (
                        <span>
                            Attempted to access {serverSection} {isHost && <>from {hostNameSection} </>}
                            Blocked, as user did not respond to MFA.
                            {responseNote && <> {responseNote}</>}
                        </span>
                    );
                } else {
                    headlineText = (
                        <span>
                            Attempted to access {serverSection} {isHost && <>from {hostNameSection} </>}
                            and was rejected by <strong>MFA</strong>.{responseNote && <> {responseNote}</>}
                        </span>
                    );
                }
            }
            break;

        case POLICY_ACTION_TYPES.NOTIFY.value:
            if (entityType === INVESTIGATION_ENTITY_TYPES.RESOURCE.id) {
                if (isDevice) {
                    headlineText = (
                        <span>
                            Used {usernameSection ? 'by ' : ''}
                            {usernameSection ? usernameSection : ''} to access {serverSection} and sent notification to
                            syslog
                        </span>
                    );
                } else {
                    headlineText = (
                        <span>
                            Accessed {usernameSection ? 'by ' : ''}
                            {usernameSection ? usernameSection : ''}
                            {isHost && 'from'} {hostNameSection} and sent notification to syslog
                        </span>
                    );
                }
            } else {
                headlineText = (
                    <span>
                        Accessed {serverSection} {isHost && <>from {hostNameSection} </>}and sent notification to
                        syslog.
                    </span>
                );
            }
            break;
        default:
            break;
    }

    return {
        headline: headlineText,
        context: baseContext,
        metadata: {
            ...data,
            policyType,
            action,
            user_response: response,
            reason,
            error_code: errorCode,
            rule_id: ruleId
        }
    };
};

const calcAction = (metadata) => {
    const [userResponse] = formatUserResponse(metadata.user_response, metadata.effective_rule_action);
    let ruleAction = formatRuleAction(
        metadata.effective_rule_action,
        metadata.effective_rule_id,
        true,
        metadata.protocol_type
    );

    const displayUserResponse = userResponse && (userResponse !== 'n/a' || ruleAction === 'MFA');
    if (ruleAction && displayUserResponse) {
        ruleAction += ` (${userResponse})`;
    }

    if (!ruleAction) {
        ruleAction = 'None';
    }

    return ruleAction;
};

const getRiskIndicatorTitle = (riskIndicatorObj, logData) => {
    let riskIndicatorTitle = riskIndicatorObj.authTitle;
    const extraDetails =
        !isEmpty(riskIndicatorObj.extraLogDetails) &&
        getExtraRiskIndicatorDescription(
            logData,
            riskIndicatorObj.extraLogDetails.extraDetailsType,
            riskIndicatorObj.extraLogDetails.extraDetailsField
        );

    if (!isEmpty(extraDetails)) {
        riskIndicatorTitle += ` (${extraDetails})`;
    }
    return riskIndicatorTitle;
};

export const buildActivityRows = ({
    rows,
    onEntityClick,
    defaultDestinations,
    relyingParties,
    logsMetadata,
    geoLocationEnabled,
    entityType,
    entityData
}) => {
    const activityRows: ActivityTableRow[] = [];

    for (const row of rows) {
        const logData = generateHeadline({
            data: row,
            onEntityClick,
            entityType,
            defaultDestinations,
            relyingParties,
            entityData
        });
        const wallName = logsMetadata?.wallNames[logData.metadata.wall_id];
        const ruleIdWithPrefix =
            logData.metadata.effective_rule_id && logData.metadata.effective_rule_type
                ? getRuleIdWithPrefix(logData.metadata.effective_rule_id, logData.metadata.effective_rule_type)
                : '';

        const isNtlmFailOpen =
            logData.metadata.protocol_type?.toLowerCase() === 'ntlm' &&
            logData.metadata.user_response?.toLowerCase() === AUTHENTICATION_LOG_REASONS.FAIL_OPEN.USER_RESPONSE &&
            !ruleIdWithPrefix;

        const locationText = isFeatureActive(geoLocationEnabled)
            ? getLocationText(logData, logsMetadata?.countriesList)
            : '';

        const filteredRiskIndicators = logData.metadata.risk_indicators_v2
            ? logData.metadata.risk_indicators_v2.filter(
                  (riskIndicator) => !logsMetadata?.hiddenRiskIndicators.includes(riskIndicator)
              )
            : [];

        const riskIndicatorsList = filteredRiskIndicators.flatMap((riskRule) => {
            const riskIndicator = getRiskIndicatorByRiskRule(riskRule);
            return riskIndicator ? riskIndicator : [];
        });

        const riskIndicators = riskIndicatorsList.map((riskIndicatorObj) => {
            return getRiskIndicatorTitle(riskIndicatorObj, logData.metadata);
        });

        const activity = {
            type: getActivityTypeText(row),
            timestamp: row.timestamp,
            log: logData.headline || '',
            data: logData.context ? (
                <div className="flex flex-col gap-4 w-full h-fit bg-sf-gray-50 py-4 px-2">
                    <div className="font-medium text-13 text-sf-gray-500">Auth</div>
                    <div>
                        <div className="text-13 text-sf-gray-500">Risk</div>
                        <div className="font-medium text-13 text-sf-gray-800">
                            {riskLevelToText(logData.metadata.risk_level)}
                        </div>
                    </div>
                    <div>
                        <div className="text-13 text-sf-gray-500">Auth Type</div>
                        <div className="font-medium text-13 text-sf-gray-800">
                            {`${
                                DIRECTORY_TYPES[logData.metadata.directory_type || DIRECTORY_TYPES.ad.key].displayName
                            } (${logData.metadata.protocol_type})`}
                        </div>
                    </div>
                    <div>
                        <div className="text-13 text-sf-gray-500">Silverfort Action</div>
                        <div className="font-medium text-13 text-sf-gray-800">{calcAction(logData.metadata)}</div>
                    </div>
                    <div className="font-medium text-13 text-sf-gray-500">Route</div>
                    {logData.metadata.domain_controller && (
                        <div>
                            <div className="text-13 text-sf-gray-500">Domain Controller</div>
                            <div className="font-medium text-13 text-sf-gray-800">
                                {logData.metadata.domain_controller?.toUpperCase() || 'None'}
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="text-13 text-sf-gray-500">Silverfort Node</div>
                        <div className="font-medium text-13 text-sf-gray-800">{wallName || 'None'}</div>
                    </div>
                    {logData.metadata.tenant_id && (
                        <div>
                            <div className="text-13 text-sf-gray-500">Tenant ID</div>
                            <div className="font-medium text-13 text-sf-gray-800">
                                {logData.metadata.tenant_id || 'None'}
                            </div>
                        </div>
                    )}

                    {isFeatureActive(geoLocationEnabled) && locationText && (
                        <div>
                            <div className="text-13 text-sf-gray-500">Location</div>
                            <div className="font-medium text-13 text-sf-gray-800">{locationText}</div>
                        </div>
                    )}
                    <div>
                        <div className="text-13 text-sf-gray-500">Request ID</div>
                        <div className="font-medium text-13 text-sf-gray-800">{logData.metadata.request_id}</div>
                    </div>
                    {(isNtlmFailOpen || logData.metadata.auth_mode) && (
                        <div>
                            <div className="text-13 text-sf-gray-500">Mode</div>
                            <div className="font-medium text-13 text-sf-gray-800">
                                {getEnumLabelByValue(AUTH_MODE_FILTERS_OPTIONS, logData.metadata.auth_mode)}
                            </div>
                        </div>
                    )}
                    {riskIndicators.length > 0 && (
                        <>
                            <div className="font-medium text-13 text-sf-gray-500">Risk</div>
                            <div>
                                <div className="text-13 text-sf-gray-500">Risk Indicators</div>
                                <div className="w-full flex flex-wrap gap-2 font-medium text-13 text-sf-gray-800">
                                    {riskIndicators.map((risk, i) => (
                                        <span key={i}>
                                            {risk}
                                            {i < riskIndicators.length - 1 ? ',' : ''}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                ''
            )
        };

        activityRows.push(activity);
    }

    return activityRows;
};
